/* eslint-disable react/no-array-index-key */
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
  Button,
  Checkbox,
  ContentSection,
  Layout,
  Paragraph,
  SectionHeading,
  UnorderedList,
} from '@vwfs-bronson/bronson-react';
import { uploadStaticFile } from '../../services/api/uploadStaticFile/uploadStaticFile';
import { getScriveLink } from '../../services/common/data';
import { debugLog } from '../../services/common/miscUtils';
import { loadingActions } from '../../services/redux/features/loading.slice';
import { useNavigate } from '../../services/routing/navigate';

interface Props {
  formData?: any;
}

const fileUrl = '/assets/electronic-signature-and-terms.pdf';

export const ESignLandingPage: FunctionComponent<Props> = () => {
  const { t } = useTranslation();

  const [scriveLink, setScriveLink] = useState<string | null>(null);
  const [checkedCheckbox, setCheckedCheckbox] = useState<boolean>(false);
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const getCIdFromUrl = (): string | null => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('cid');
  };

  const fetchPdfAsBase64 = async (): Promise<string> => {
    const response = await fetch(fileUrl);
    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const arrayBuffer = reader.result as ArrayBuffer;
        const uint8Array = new Uint8Array(arrayBuffer);

        let binaryString = '';
        for (let i = 0; i < uint8Array.length; i++) {
          binaryString += String.fromCharCode(uint8Array[i]);
        }
        const base64String = btoa(binaryString);
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(blob);
    });
  };

  const handleFileUpload = async (): Promise<void> => {
    const cId = getCIdFromUrl();

    if (!cId) {
      throw new Error('cId URL parameter is missing');
    }

    const base64Content = await fetchPdfAsBase64();

    await uploadStaticFile({
      transactionId: cId,
      file: {
        name: 'Convention de preuve et CGU signature électronique.pdf',
        mimeType: 'application/pdf',
        base64: base64Content,
        tags: ['SCRIVE_DOCUMENT_REQUEST'],
        expirationDays: 15,
      },
      owners: ['vwfs-fr-miles'],
    });
  };

  useEffect(() => {
    const scriveLink = getScriveLink();
    if (!scriveLink) {
      navigate.error();
      throw new Error('Query parameter is missing');
    }

    setScriveLink(scriveLink);
  }, [navigate]);

  const handleButtonClick = async () => {
    if (!scriveLink) {
      return;
    }

    dispatch(loadingActions.setLoading(true));

    try {
      await handleFileUpload();
      dispatch(loadingActions.setLoading(false));
      window.location.href = scriveLink;
    } catch (error) {
      dispatch(loadingActions.setLoading(false));
      debugLog({ message: 'Upload failed:', level: 'ERROR' });
      window.location.href = scriveLink;
    }
  };

  return (
    <>
      {/*  https://jira.platform.vwfs.io/browse/PC-2826
      <ContentSection pageWrap>
        <ShoppingCart formData={formData} dataTheme="default" />
      </ContentSection> */}
      <ContentSection pageWrapSize="small" className="u-pb">
        <SectionHeading level={3}>{t('eSignLandingPage:mainTitle')}</SectionHeading>
        <Layout center className="u-mt-large">
          <Layout.Item>
            <SectionHeading level={4} primary>
              {t('eSignLandingPage:successMessage')}
            </SectionHeading>
          </Layout.Item>
        </Layout>
        <Layout center>
          <Layout.Item>
            <Paragraph>{t('eSignLandingPage:signingInfo')}</Paragraph>
          </Layout.Item>
        </Layout>
      </ContentSection>
      <ContentSection pageWrapSize="small" className="u-pv-none u-font-size-fs2">
        <Paragraph> {t('eSignLandingPage:signingChecklist')}</Paragraph>
        <Layout center className="u-mt">
          <Layout.Item>
            <UnorderedList>
              <UnorderedList.Item>{t('eSignLandingPage:item1')}</UnorderedList.Item>
              <UnorderedList.Item>{t('eSignLandingPage:item2')}</UnorderedList.Item>
            </UnorderedList>
          </Layout.Item>
        </Layout>

        <Checkbox checked={checkedCheckbox} onChange={() => setCheckedCheckbox(!checkedCheckbox)}>
          <Paragraph className="u-mt u-text-brand">
            {t('eSignLandingPage:generalConditionsNotice:prefix')}
            <a href={fileUrl} target="_blank" rel="noreferrer" onClick={() => setCheckedCheckbox(true)}>
              {t('eSignLandingPage:generalConditionsNotice:linkText')}
            </a>
          </Paragraph>
        </Checkbox>
      </ContentSection>
      <ContentSection className="u-pt-small">
        <Layout center>
          <Layout.Item default="1/4">
            <Button type="button" disabled={!checkedCheckbox} full onClick={handleButtonClick}>
              {t('eSignLandingPage:next')}
            </Button>
          </Layout.Item>
        </Layout>
      </ContentSection>
      <ContentSection pageWrapSize="small" className="u-pt-small">
        <Paragraph> {t('eSignLandingPage:signatureFallbackMessage')}</Paragraph>
      </ContentSection>
    </>
  );
};
